import { endOfDay, startOfDay, subDays } from "date-fns";

import type { DateRangePreset, LayerConfiguration, LayerDefinition } from "../../../models";

export const SALE_WINDOW_HOURS = 1;

export const DATE_RANGE_OPTIONS: { id: DateRangePreset | ""; value: string }[] = [
	{ id: "", value: "Custom" },
	{ id: "today", value: "Today" },
	{ id: "yesterday", value: "Yesterday" },
	{ id: "last7Days", value: "Last 7 Days" },
	{ id: "last30Days", value: "Last 30 Days" },
	{ id: "aroundSale", value: `Enrollment Sale (+/- ${SALE_WINDOW_HOURS} Hour)` },
];

const PRESET_DAYS_AGO: Partial<Record<DateRangePreset, [start: number, end: number]>> = {
	today: [0, 0],
	yesterday: [1, 1],
	last7Days: [6, 0],
	last30Days: [29, 0],
};

type DateFilters = Pick<LayerConfiguration, "dateRange" | "startDateTime" | "endDateTime">;

export function dateRangeLabel(dateRange: DateRangePreset | undefined) {
	return dateRange ? DATE_RANGE_OPTIONS.find((option) => option.id === dateRange)?.value : undefined;
}

/** Presets resolve to whole local days, so a request built from them keeps the same query key all day. */
export function resolveDateRange(
	filters: DateFilters,
	now = new Date(),
): Pick<DateFilters, "startDateTime" | "endDateTime"> {
	if (filters.dateRange === "aroundSale") return { startDateTime: undefined, endDateTime: undefined };
	const daysAgo = PRESET_DAYS_AGO[filters.dateRange];
	if (!daysAgo) return { startDateTime: filters.startDateTime, endDateTime: filters.endDateTime };
	return { startDateTime: startOfDay(subDays(now, daysAgo[0])), endDateTime: endOfDay(subDays(now, daysAgo[1])) };
}

/** The sale window needs an enrollment to center on, so such a layer loads nothing until one is entered. */
export function awaitsEnrollment(filters: Pick<LayerConfiguration, "dateRange" | "enrollmentId">) {
	return filters.dateRange === "aroundSale" && !filters.enrollmentId;
}

/** Layers centered on the sale share one Enrollment ID, like the single Enrollment box on the old map page. */
export function sharedEnrollmentLayerIds(layers: LayerDefinition[], layerId: string) {
	if (!layers.some((layer) => layer.id === layerId && isSaleCentered(layer))) return [layerId];
	return layers.filter(isSaleCentered).map((layer) => layer.id);
}

function isSaleCentered(layer: LayerDefinition) {
	return layer.dateRange === "aroundSale";
}
